import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    ArrowBack,
    DeleteOutline,
    EditOutlined,
    ExpandLess,
    ExpandMore,
} from "@mui/icons-material";
import { type DropResult } from "react-beautiful-dnd";
import RubricManagementList, { Column, type StatusFilter } from "../../molecules/RubricManagementList";
import { useListState } from "../../../hooks/useListState";
import QuestionsSection from "./CodeWorkflow/QuestionsSection";
import {
    supportsCrossWorkflowSources,
    supportsCrossWorkflowTargets,
} from "./CodeWorkflow/workflowTriggerCapabilities";
import {
    type CodeWorkflow,
    type CompoundCondition,
    type ConditionClause,
    type Question,
    type WorkflowQuestion,
    type WorkflowTrigger,
    type CodeWorkflowType,
    type SpanWorkflowQuestionScope,
    WorkflowTriggerAction,
    QuestionType,
} from "../../../types/rubric";
import type { AnswerValue } from "../../../types/answerSet";
import {
    listCodeWorkflowsPaginated,
    listCodeWorkflows,
    fetchCodeWorkflow,
    createCodeWorkflow,
    updateCodeWorkflow,
    deleteCodeWorkflow,
    fetchQuestions,
    fetchQuestion,
} from "../../../utils/api";
type ViewMode = "list" | "detail";

interface CodeWorkflowsPanelProps {
    onPathChange?: (path: string) => void;
    rubricVersionId?: number;
    /* option props particularly useful for unit testing */
    initialViewMode?: ViewMode;
    initialFormState?: Partial<WorkflowFormState>;
}

export type WorkflowQuestionWithClientId = WorkflowQuestion & {
    clientId: string;
}

export interface WorkflowFormState {
    name: string;
    description: string;
    codeType: CodeWorkflowType;
    active: boolean;
    systemQuestions: WorkflowQuestionWithClientId[];
    workflowQuestions: WorkflowQuestionWithClientId[];
}

const generateClientId = () => `workflow-question-${Math.random().toString(36).slice(2)}-${Date.now()}`;
const generateTriggerClientId = () => `trigger-${Math.random().toString(36).slice(2)}-${Date.now()}`;

const withClientIds = (questions: WorkflowQuestion[] | undefined | null): WorkflowQuestionWithClientId[] =>
    (questions ?? []).map((question) => ({
        ...question,
        clientId: (question as WorkflowQuestionWithClientId).clientId ?? generateClientId(),
        spanScope: question.spanScope ?? "ALL_SPANS",
        spanScopeQuestionId: question.spanScope === "SELECTED_BY_SPANS_QUESTION"
            ? question.spanScopeQuestionId ?? null
            : null,
        triggers: question.triggers?.map((trigger) => ({
            ...trigger,
            triggerPhase: trigger.triggerPhase ?? "POST",
            _clientId: trigger._clientId ?? generateTriggerClientId(),
            // Upgrade legacy triggers to compound format so they support "Add Condition"
            // Use trigger's fromQuestionId if set (cross-question), otherwise default to host question
            compoundCondition: trigger.compoundCondition ?? {
                operator: "AND" as const,
                conditions: [{ questionId: trigger.fromQuestionId ?? question.question.id, answerValueIds: trigger.answerValueIds ?? [] }],
            },
        })),
    }));

const validateWorkflowBeforeSave = (
    questions: WorkflowQuestionWithClientId[],
    // [CDMX-14572] Cross-workflow source questions (code/case workflows reachable
    // via the SPAN-host source picker) are valid condition question references
    // but are not members of the host workflow.
    // Pass their ids in so the host-membership and ordering checks treat them
    // as always-valid (no order constraint applies — they live on a sibling
    // workflow with its own runtime resolution).
    extraValidQuestionIds: ReadonlySet<number> = new Set(),
): string | null => {
    const questionNames = new Map(questions.map((workflowQuestion) => [workflowQuestion.question.id, workflowQuestion.question.name]));
    const questionOrderById = new Map(
        questions.map((workflowQuestion) => [workflowQuestion.question.id, workflowQuestion.sequenceOrder ?? null])
    );
    const isHostQuestion = (id: number) => questionOrderById.has(id);
    const isExtraValid = (id: number) => extraValidQuestionIds.has(id);

    for (const workflowQuestion of questions) {
        const hostQuestionName = workflowQuestion.question.name;
        const hostQuestionOrder = workflowQuestion.sequenceOrder;

        for (const trigger of workflowQuestion.triggers ?? []) {
            if (!trigger.compoundCondition && hostQuestionOrder != null && trigger.fromQuestionId != null) {
                const fromId = trigger.fromQuestionId;
                if (!isHostQuestion(fromId) && !isExtraValid(fromId)) {
                    return `Trigger on "${hostQuestionName}" must reference a question that is still in the workflow`;
                }
                // Order constraint applies only when the source is a host-workflow question.
                if (isHostQuestion(fromId)) {
                    const referencedOrder = questionOrderById.get(fromId);
                    if (referencedOrder != null && referencedOrder > hostQuestionOrder) {
                        return `Trigger on "${hostQuestionName}" must reference the same question or an earlier question`;
                    }
                }
            }

            if (trigger.compoundCondition) {
                if (!trigger.compoundCondition.conditions.length) {
                    return `Trigger on "${hostQuestionName}" must include at least one condition`;
                }

                for (const clause of trigger.compoundCondition.conditions) {
                    if (clause.questionId == null) {
                        return `Trigger on "${hostQuestionName}" must include a condition question`;
                    }

                    if (!isHostQuestion(clause.questionId) && !isExtraValid(clause.questionId)) {
                        return `Trigger on "${hostQuestionName}" must reference a question that is still in the workflow`;
                    }

                    // Order constraint applies only to host-workflow references; cross-workflow
                    // sources resolve outside the host's question ordering.
                    if (hostQuestionOrder != null && isHostQuestion(clause.questionId)) {
                        const referencedOrder = questionOrderById.get(clause.questionId);
                        if (referencedOrder != null && referencedOrder > hostQuestionOrder) {
                            return `Trigger on "${hostQuestionName}" must reference the same question or an earlier question`;
                        }
                    }

                    if (!clause.answerValueIds?.length) {
                        const clauseQuestionName = questionNames.get(clause.questionId);
                        return clauseQuestionName
                            ? `Condition on "${hostQuestionName}" for "${clauseQuestionName}" must include at least one answer`
                            : `Condition on "${hostQuestionName}" must include at least one answer`;
                    }
                }

                continue;
            }

            if (!trigger.answerValueIds?.length) {
                return `Trigger on "${hostQuestionName}" must include at least one answer`;
            }
        }
    }

    return null;
};

const getFormQuestionIds = (questions: WorkflowQuestion[]): ReadonlySet<number> => new Set(
    questions
        .map((workflowQuestion) => workflowQuestion.question?.id)
        .filter((id): id is number => id != null)
);

const isPreShowQuestionTrigger = (trigger: WorkflowTrigger): boolean =>
    (trigger.triggerPhase ?? "POST") === "PRE" &&
    trigger.action === WorkflowTriggerAction.SHOW_QUESTION;

const supportsLegacyCrossWorkflowSourceReference = (
    trigger: WorkflowTrigger,
    hostCodeType: CodeWorkflowType,
): boolean =>
    hostCodeType === "SPAN" &&
    trigger.id != null &&
    (trigger.triggerPhase ?? "POST") === "POST" &&
    (
        trigger.action === WorkflowTriggerAction.GO_TO ||
        trigger.action === WorkflowTriggerAction.STOP
    );

const canUseCrossWorkflowSourceReference = (
    trigger: WorkflowTrigger,
    hostCodeType: CodeWorkflowType,
): boolean =>
    supportsCrossWorkflowSources(trigger.action, trigger.triggerPhase ?? "POST", hostCodeType) ||
    supportsLegacyCrossWorkflowSourceReference(trigger, hostCodeType);

const getSelectedCrossWorkflowSourceQuestionIds = (
    questions: WorkflowQuestion[],
    hostCodeType: CodeWorkflowType,
): ReadonlySet<number> => {
    if (hostCodeType !== "SPAN") {
        return new Set();
    }

    const hostQuestionIds = getFormQuestionIds(questions);
    const externalSourceIds = new Set<number>();
    for (const workflowQuestion of questions) {
        for (const trigger of workflowQuestion.triggers ?? []) {
            if (!canUseCrossWorkflowSourceReference(trigger, hostCodeType)) {
                continue;
            }

            if (trigger.compoundCondition) {
                for (const condition of trigger.compoundCondition.conditions) {
                    if (condition.questionId != null && !hostQuestionIds.has(condition.questionId)) {
                        externalSourceIds.add(condition.questionId);
                    }
                }
                continue;
            }

            if (trigger.fromQuestionId != null && !hostQuestionIds.has(trigger.fromQuestionId)) {
                externalSourceIds.add(trigger.fromQuestionId);
            }
        }
    }

    return externalSourceIds;
};

const removePreShowQuestionTriggers = <T extends WorkflowQuestion>(workflowQuestion: T): T => {
    if (!workflowQuestion.triggers?.some(isPreShowQuestionTrigger)) {
        return workflowQuestion;
    }

    return {
        ...workflowQuestion,
        triggers: workflowQuestion.triggers
            .filter((trigger) => !isPreShowQuestionTrigger(trigger))
            .map((trigger, index) => ({
                ...trigger,
                triggerOrder: index,
            })),
    };
};

const getHostAnswerValueIds = (workflowQuestion: WorkflowQuestion): ReadonlySet<number> => new Set(
    (workflowQuestion.question?.answers ?? [])
        .map((answer) => answer.id)
        .filter((id): id is number => id != null)
);

const getResetSourceAnswerValueIds = (
    trigger: WorkflowTrigger,
    hostAnswerValueIds: ReadonlySet<number>
): number[] => {
    const candidates = [
        ...(trigger.answerValueIds ?? []),
        ...(trigger.compoundCondition?.conditions ?? []).flatMap((condition) => condition.answerValueIds ?? []),
    ];
    const seen = new Set<number>();
    return candidates.filter((answerId) => {
        if (!hostAnswerValueIds.has(answerId) || seen.has(answerId)) {
            return false;
        }
        seen.add(answerId);
        return true;
    });
};

const sanitizeTriggerSourceScope = (
    trigger: WorkflowTrigger,
    hostQuestionId: number,
    hostQuestionIds: ReadonlySet<number>,
    hostAnswerValueIds: ReadonlySet<number>,
    allowCrossWorkflowSources: boolean
): WorkflowTrigger => {
    if (allowCrossWorkflowSources) {
        return trigger;
    }

    const resetAnswerValueIds = getResetSourceAnswerValueIds(trigger, hostAnswerValueIds);
    const resetCondition: ConditionClause = {
        questionId: hostQuestionId,
        answerValueIds: resetAnswerValueIds,
    };

    if (trigger.compoundCondition) {
        let changed = false;
        const conditions = trigger.compoundCondition.conditions.map((condition) => {
            if (hostQuestionIds.has(condition.questionId)) {
                return condition;
            }
            changed = true;
            return resetCondition;
        });
        const fromQuestionId =
            trigger.fromQuestionId != null && !hostQuestionIds.has(trigger.fromQuestionId)
                ? hostQuestionId
                : trigger.fromQuestionId;

        if (!changed && fromQuestionId === trigger.fromQuestionId) {
            return trigger;
        }

        return {
            ...trigger,
            fromQuestionId,
            answerValueIds: resetAnswerValueIds,
            compoundCondition: {
                ...trigger.compoundCondition,
                conditions,
            },
        };
    }

    if (trigger.fromQuestionId != null && !hostQuestionIds.has(trigger.fromQuestionId)) {
        return {
            ...trigger,
            fromQuestionId: undefined,
            answerValueIds: resetAnswerValueIds,
        };
    }

    return trigger;
};

const sanitizeWorkflowQuestionSourceScope = <T extends WorkflowQuestion>(
    workflowQuestion: T,
    hostQuestionIds: ReadonlySet<number>,
    hostCodeType: CodeWorkflowType,
): T => {
    const hostQuestionId = workflowQuestion.question?.id;
    if (hostQuestionId == null || !workflowQuestion.triggers?.length) {
        return workflowQuestion;
    }

    const hostAnswerValueIds = getHostAnswerValueIds(workflowQuestion);
    return {
        ...workflowQuestion,
        triggers: workflowQuestion.triggers.map((trigger) =>
            sanitizeTriggerSourceScope(
                trigger,
                hostQuestionId,
                hostQuestionIds,
                hostAnswerValueIds,
                canUseCrossWorkflowSourceReference(trigger, hostCodeType),
            )
        ),
    };
};

const sanitizeWorkflowQuestionsForSave = (questions: WorkflowQuestion[]): WorkflowQuestion[] => {
    const hostQuestionIds = new Set(
        questions
            .map((question) => question.question?.id)
            .filter((id): id is number => id != null)
    );

    return questions.map((question) => ({
        ...question,
        triggers: question.triggers?.map((trigger) => {
            // The backend rejects {compoundCondition != null + action == AUTOFILL}
            // outright (`AUTOFILL triggers cannot use compound conditions; use a simple
            // answerValueIds list instead`), but withClientIds upgrades every loaded
            // trigger into compound shape so the editor can render uniformly. For
            // AUTOFILL with a single clause that compound shape is semantically a simple
            // condition, so downgrade it back to the legacy simple form before sending
            // to the backend. Other actions (STOP / GO_TO / SET_ANSWERS) accept both
            // shapes, so we leave their compound shape intact to preserve the user's
            // chosen authoring (operator, clause order, future Add Condition).
            let sanitizedTrigger;
            if (
                trigger.action === WorkflowTriggerAction.AUTOFILL
                && trigger.compoundCondition
                && trigger.compoundCondition.conditions.length === 1
            ) {
                const clause = trigger.compoundCondition.conditions[0];
                sanitizedTrigger = {
                    ...trigger,
                    compoundCondition: undefined,
                    fromQuestionId: clause.questionId,
                    answerValueIds: clause.answerValueIds,
                };
            } else if (trigger.compoundCondition) {
                sanitizedTrigger = {
                    ...trigger,
                    fromQuestionId: undefined,
                    answerValueIds: undefined,
                };
            } else {
                sanitizedTrigger = trigger;
            }
            const targetQuestionId = sanitizedTrigger.toQuestionId;
            const targetIsOutsideHostWorkflow =
                targetQuestionId != null && !hostQuestionIds.has(targetQuestionId);
            if (
                targetIsOutsideHostWorkflow &&
                !supportsCrossWorkflowTargets(sanitizedTrigger.action)
            ) {
                return {
                    ...sanitizedTrigger,
                    toQuestionId: undefined,
                    toAnswerValueIds: [],
                };
            }
            return sanitizedTrigger;
        }),
    }));
};

const initialFormState: WorkflowFormState = {
    name: "",
    description: "",
    codeType: "PROCEDURE",
    active: true,
    systemQuestions: [],
    workflowQuestions: [],
};

const codeTypeOptions: { value: CodeWorkflowType; label: string }[] = [
    { value: "PROCEDURE", label: "Procedure" },
    { value: "MODIFIER", label: "Modifier" },
    { value: "DIAGNOSIS", label: "Diagnosis" },
    { value: "CASE", label: "Case" },
    { value: "SPAN", label: "Span" },
];

// Helper to get code type styling
const getCodeTypeStyle = (codeType: CodeWorkflowType | undefined) => {
    const styles: Record<CodeWorkflowType, { bg: string; color: string }> = {
        PROCEDURE: { bg: "#F3EAF2", color: "#875882" },
        MODIFIER: { bg: "#EAF2FF", color: "#186AE1" },
        DIAGNOSIS: { bg: "#E0FCF0", color: "#26835B" },
        CASE: { bg: "#F3E5F5", color: "#E5927D" },
        SPAN: { bg: "#FFF3E0", color: "#E65100" },
    };
    return styles[codeType ?? "PROCEDURE"] ?? styles.PROCEDURE;
};

// Column configuration for the workflows list - single flowing layout
const workflowColumns: Column<CodeWorkflow>[] = [
    {
        field: "name",
        label: "Name",
        sortable: true,
        width: "flex",
        render: (item) => {
            const style = getCodeTypeStyle(item.codeType as CodeWorkflowType);
            return (
                <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body1">
                            {item.name}
                        </Typography>
                        <Box
                            sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: "3px",
                                backgroundColor: style.bg,
                                color: style.color,
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                textTransform: "capitalize",
                                display: "inline-block",
                            }}
                        >
                            {item.codeType?.toLowerCase()}
                        </Box>
                    </Stack>
                    {item.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {item.description}
                        </Typography>
                    )}
                </Box>
            );
        },
    },
];

const CodeWorkflowsPanel: React.FC<CodeWorkflowsPanelProps> = ({ 
    onPathChange,
    rubricVersionId,
    initialViewMode = "list",
    initialFormState: initialFormStateProp,
}) => {
    const [workflows, setWorkflows] = useState<CodeWorkflow[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [questions, setQuestions] = useState<Question[]>([]);
    const [questionCache, setQuestionCache] = useState<Record<number, Question>>({});
    const [viewMode, setViewMode] = useState<ViewMode>(initialViewMode);
    const [selectedWorkflow, setSelectedWorkflow] = useState<CodeWorkflow | null>(null);
    const [formState, setFormState] = useState<WorkflowFormState>(() => {
        const allQuestions = withClientIds((initialFormStateProp?.systemQuestions ?? []).concat(initialFormStateProp?.workflowQuestions ?? []));
        return {
            ...initialFormState,
            ...initialFormStateProp ?? {},
            systemQuestions: withClientIds(
                allQuestions.filter(q => q.question.questionType === QuestionType.SYSTEM)
            ),
            workflowQuestions: withClientIds(
                allQuestions.filter(q => q.question.questionType !== QuestionType.SYSTEM)
            ),
        };
    });
    const [loading, setLoading] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    // True when editing an existing workflow whose detail fetch failed. The
    // panel still renders the editor shell + error Alert (so the user sees
    // feedback) but Save must be disabled — saving from list-level fallback
    // state could PUT a workflow with stripped questions/triggers and clear
    // them on the backend.
    const [detailLoadFailed, setDetailLoadFailed] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
    const [questionsSectionCollapsed, setQuestionsSectionCollapsed] = useState(false);
    const [systemQuestionsSectionCollapsed, setSystemQuestionsSectionCollapsed] = useState(false);

    // Use URL-based list state for pagination, sorting, and search
    const listState = useListState({ defaultSortBy: "name", defaultSortDirection: "asc", paramPrefix: "cw" });
    const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

    // Combined list for trigger target options (system questions first, then workflow questions)
    const allFormQuestions = useMemo(() => {
        return [...formState.systemQuestions, ...formState.workflowQuestions];
    }, [formState.systemQuestions, formState.workflowQuestions]);

    // Cross-workflow target questions (PROCEDURE/DIAGNOSIS/MODIFIER from sibling
    // workflows). Populated only when the host workflow is CASE; surfaced in the
    // trigger Target picker only for actions that support cross-workflow targets.
    // AUTOFILL is the only supported action today. SPAN workflows are excluded.
    const [crossWorkflowQuestions, setCrossWorkflowQuestions] = useState<WorkflowQuestionWithClientId[]>([]);
    const [crossWorkflowQuestionsLoading, setCrossWorkflowQuestionsLoading] = useState(false);

    // [CDMX-14572] Cross-workflow SOURCE questions for SPAN-typed host
    // workflows. The trigger "from question" picker widens for SPAN hosts
    // on GO_TO/STOP to include grouped CASE / code-level (PROCEDURE/
    // DIAGNOSIS/MODIFIER) sibling questions. Mirrors the rubric-agnostic
    // loader pattern from CDMX-13958 (cross-workflow target picker) but
    // segregates entries by source workflow's codeType so the picker can
    // render "From code workflows" vs "From case workflows" group
    // headers without inspecting codeType at render time.
    const [crossWorkflowSourceQuestions, setCrossWorkflowSourceQuestions] = useState<{
        codeWorkflows: WorkflowQuestionWithClientId[];
        caseWorkflows: WorkflowQuestionWithClientId[];
    }>({ codeWorkflows: [], caseWorkflows: [] });

    const availableWorkflowQuestions = useMemo(() => {
        return questions.filter((question) => question.questionType !== QuestionType.SYSTEM);
    }, [questions]);

    const availableSystemQuestions = useMemo(() => {
        return questions.filter((question) => question.questionType === QuestionType.SYSTEM);
    }, [questions]);

    useEffect(() => {
        const path =
            viewMode === "list"
                ? "Workflows"
                : selectedWorkflow
                    ? "Update Workflow"
                    : "Create Workflow";
        onPathChange?.(path);
    }, [viewMode, selectedWorkflow, onPathChange]);

    const loadWorkflows = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await listCodeWorkflowsPaginated({
                page: listState.page,
                size: listState.pageSize,
                sortBy: listState.sortBy,
                sortDirection: listState.sortDirection,
                search: listState.search || undefined,
                active: statusFilter === "active" ? true : statusFilter === "inactive" ? false : undefined,
            });
            setWorkflows(response.content ?? []);
            setTotalCount(response.totalElements);
        } catch (err) {
            console.error("Failed to load workflows", err);
            setError("Unable to load workflows");
        } finally {
            setLoading(false);
        }
    }, [listState.page, listState.pageSize, listState.sortBy, listState.sortDirection, listState.search, statusFilter]);

    const loadQuestions = useCallback(async () => {
        try {
            const list = await fetchQuestions({ active: true });
            setQuestions(list ?? []);
        } catch (err) {
            console.error("Failed to load questions", err);
        }
    }, []);

    useEffect(() => {
        loadQuestions();
    }, [loadQuestions]);

    useEffect(() => {
        loadWorkflows();
    }, [loadWorkflows]);

    // Load cross-workflow target questions only when the caller provides a
    // rubric version context. The backend rejects targets outside the host
    // workflow's rubric version, so the global Workflows tab must not surface
    // unscoped cross-workflow choices.
    useEffect(() => {
        let cancelled = false;
        if (viewMode !== "detail" || formState.codeType !== "CASE") {
            setCrossWorkflowQuestions([]);
            setCrossWorkflowQuestionsLoading(false);
            return () => {
                cancelled = true;
            };
        }
        const load = async () => {
            setCrossWorkflowQuestionsLoading(true);
            try {
                const all = await listCodeWorkflows({ active: true });
                const hostId = selectedWorkflow?.id;
                const targetWorkflows = (all ?? []).filter((wf) => {
                    if (wf.id === hostId) return false;
                    return (
                        wf.codeType === "PROCEDURE" ||
                        wf.codeType === "DIAGNOSIS" ||
                        wf.codeType === "MODIFIER"
                    );
                });
                // Fetch detail (with questions + answers) for each. The list
                // endpoint may already return questions, but normalize via
                // fetchCodeWorkflow when a workflow's questions are missing so
                // every option in the picker has the answers needed for the
                // toAnswerValueIds dropdown.
                const detailed = await Promise.all(
                    targetWorkflows.map(async (wf) => {
                        if (wf.questions && wf.questions.length > 0) {
                            return wf;
                        }
                        if (wf.id == null) return wf;
                        try {
                            return await fetchCodeWorkflow(wf.id);
                        } catch (err) {
                            console.warn(`Failed to load workflow ${wf.id} detail`, err);
                            return wf;
                        }
                    })
                );
                if (cancelled) return;
                const flat: WorkflowQuestionWithClientId[] = [];
                for (const wf of detailed) {
                    const wfName = wf.name ?? `Workflow ${wf.id ?? ""}`;
                    for (const wq of wf.questions ?? []) {
                        if (!wq.question?.id) continue;
                        flat.push({
                            ...wq,
                            clientId: `xwf-${wf.id ?? "unknown"}-${wq.question.id}`,
                            question: {
                                ...wq.question,
                                // Prefix the rendered label so the picker
                                // disambiguates same-named questions from
                                // different workflows.
                                name: `${wfName}: ${wq.question.name}`,
                            },
                        });
                    }
                }
                setCrossWorkflowQuestions(flat);
            } catch (err) {
                console.warn("Failed to load cross-workflow target questions", err);
                if (!cancelled) {
                    setCrossWorkflowQuestions([]);
                }
            } finally {
                if (!cancelled) {
                    setCrossWorkflowQuestionsLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [viewMode, formState.codeType, selectedWorkflow?.id]);

    // [CDMX-14572] Load cross-workflow SOURCE questions when editing a
    // SPAN-typed workflow. The pool spans CASE / PROCEDURE / DIAGNOSIS /
    // MODIFIER sibling workflows (excluding SPAN itself — span-typed
    // questions are not resolvable as cross-workflow sources by the
    // trigger evaluator). Pre-segregated by codeType so QuestionsSection
    // can render "From code workflows" / "From case workflows"
    // groupBy headers without inspecting codeType at render time.
    //
    // Mirrors the target-side loader's rubric-agnostic posture (CDMX-13958):
    // the picker surfaces all active sibling workflows; runtime grading
    // enforces rubric-version scope and silently no-ops when the chosen
    // source workflow isn't co-assigned. F2c surfaces a non-blocking
    // warning in the trigger builder so authors catch that mismatch.
    useEffect(() => {
        let cancelled = false;
        if (viewMode !== "detail" || formState.codeType !== "SPAN") {
            setCrossWorkflowSourceQuestions({ codeWorkflows: [], caseWorkflows: [] });
            return () => {
                cancelled = true;
            };
        }
        const load = async () => {
            try {
                const all = await listCodeWorkflows({ active: true });
                const hostId = selectedWorkflow?.id;
                const sourceWorkflows = (all ?? []).filter((wf) => {
                    if (wf.id === hostId) return false;
                    return (
                        wf.codeType === "CASE" ||
                        wf.codeType === "PROCEDURE" ||
                        wf.codeType === "DIAGNOSIS" ||
                        wf.codeType === "MODIFIER"
                    );
                });
                const detailed = await Promise.all(
                    sourceWorkflows.map(async (wf) => {
                        if (wf.questions && wf.questions.length > 0) {
                            return wf;
                        }
                        if (wf.id == null) return wf;
                        try {
                            return await fetchCodeWorkflow(wf.id);
                        } catch (err) {
                            console.warn(`Failed to load workflow ${wf.id} detail`, err);
                            return wf;
                        }
                    })
                );
                if (cancelled) return;
                const codeFlat: WorkflowQuestionWithClientId[] = [];
                const caseFlat: WorkflowQuestionWithClientId[] = [];
                for (const wf of detailed) {
                    const wfName = wf.name ?? `Workflow ${wf.id ?? ""}`;
                        const bucket =
                            wf.codeType === "CASE" ? caseFlat : codeFlat;
                    for (const wq of wf.questions ?? []) {
                        if (!wq.question?.id) continue;
                        if (
                            wq.question.questionType === QuestionType.SPAN
                        ) {
                            continue;
                        }
                        bucket.push({
                            ...wq,
                            clientId: `xwf-src-${wf.id ?? "unknown"}-${wq.question.id}`,
                            question: {
                                ...wq.question,
                                // Prefix the rendered label so the picker
                                // disambiguates same-named questions from
                                // different workflows.
                                name: `${wfName}: ${wq.question.name}`,
                            },
                        });
                    }
                }
                setCrossWorkflowSourceQuestions({
                    codeWorkflows: codeFlat,
                    caseWorkflows: caseFlat,
                });
            } catch (err) {
                console.warn("Failed to load cross-workflow source questions", err);
                if (!cancelled) {
                    setCrossWorkflowSourceQuestions({ codeWorkflows: [], caseWorkflows: [] });
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [viewMode, formState.codeType, selectedWorkflow?.id]);

    const resetForm = () => {
        setFormState({
            ...initialFormState,
            systemQuestions: [],
            workflowQuestions: [],
        });
        setSelectedWorkflow(null);
        setFormError(null);
        setDetailLoadFailed(false);
        setCollapsedSections({});
    };

    const handleCreateNew = () => {
        resetForm();
        setViewMode("detail");
    };

    const handleBackToList = () => {
        setViewMode("list");
        resetForm();
    };

    const ensureQuestionDetail = useCallback(
        async (questionId: number): Promise<Question> => {
            if (questionCache[questionId]) {
                return questionCache[questionId];
            }
            const detail = await fetchQuestion(questionId);
            setQuestionCache((prev) => ({ ...prev, [questionId]: detail }));
            return detail;
        },
        [questionCache]
    );

    const handleAddQuestion = async (questionId: number) => {
        if (!questionId) {
            return;
        }
        try {
            const detail = await ensureQuestionDetail(questionId);
            const isSystemQuestion = detail.questionType === QuestionType.SYSTEM;
            const newQuestion: WorkflowQuestionWithClientId = {
                sequenceOrder: 0,
                question: detail,
                triggers: [],
                clientId: generateClientId(),
            };

            setFormState((prev) => {
                if (isSystemQuestion) {
                    return {
                        ...prev,
                        systemQuestions: [...prev.systemQuestions, newQuestion],
                    };
                }
                return {
                    ...prev,
                    workflowQuestions: [...prev.workflowQuestions, newQuestion],
                };
            });
        } catch (err) {
            console.error("Failed to load question detail", err);
            setFormError("Unable to load question details");
        }
    };

    const handleRemoveQuestion = (clientId: string) => {
        setFormState((prev) => ({
            ...prev,
            systemQuestions: prev.systemQuestions.filter((q) => q.clientId !== clientId),
            workflowQuestions: prev.workflowQuestions.filter((q) => q.clientId !== clientId),
        }));
    };

    const handleSystemQuestionDragEnd = useCallback(
        (result: DropResult) => {
            if (!result.destination || result.destination.index === result.source.index) {
                return;
            }
            setFormState((prev) => {
                const updated = [...prev.systemQuestions];
                const [moved] = updated.splice(result.source.index, 1);
                if (!moved) {
                    return prev;
                }
                updated.splice(result.destination!.index, 0, moved);
                return { ...prev, systemQuestions: updated };
            });
        },
        []
    );

    const handleWorkflowQuestionDragEnd = useCallback(
        (result: DropResult) => {
            if (!result.destination || result.destination.index === result.source.index) {
                return;
            }
            setFormState((prev) => {
                const updated = [...prev.workflowQuestions];
                const [moved] = updated.splice(result.source.index, 1);
                if (!moved) {
                    return prev;
                }
                updated.splice(result.destination!.index, 0, moved);
                return { ...prev, workflowQuestions: updated };
            });
        },
        []
    );

    const handleTriggerDragEnd = useCallback(
        (questionClientId: string, sourceIndex: number, destinationIndex: number) => {
            if (sourceIndex === destinationIndex) {
                return;
            }
            setFormState((prev) => {
                const updateQuestions = (questions: WorkflowQuestionWithClientId[]) => {
                    const qIdx = questions.findIndex((q) => q.clientId === questionClientId);
                    if (qIdx === -1) return questions;
                    const question = questions[qIdx];
                    const triggers = question.triggers ? [...question.triggers] : [];
                    const [moved] = triggers.splice(sourceIndex, 1);
                    if (!moved) return questions;
                    triggers.splice(destinationIndex, 0, moved);
                    const reordered = triggers.map((t, idx) => ({ ...t, triggerOrder: idx }));
                    const updated = [...questions];
                    updated[qIdx] = { ...question, triggers: reordered };
                    return updated;
                };
                return {
                    ...prev,
                    systemQuestions: updateQuestions(prev.systemQuestions),
                    workflowQuestions: updateQuestions(prev.workflowQuestions),
                };
            });
        },
        []
    );

    const handleAddTrigger = (clientId: string) => {
        setFormState((prev) => {
            const updateQuestions = (questions: WorkflowQuestionWithClientId[]) => {
                const index = questions.findIndex((q) => q.clientId === clientId);
                if (index === -1) return questions;
                const question = questions[index];
                // SYSTEM questions use synthetic true/false triggers and
                // SPAN/SPANS questions use sentinel condition IDs, so skip the answers check
                const skipAnswersCheck = question.question.questionType === QuestionType.SYSTEM ||
                    question.question.questionType === QuestionType.SPAN ||
                    question.question.questionType === QuestionType.SPANS;
                if (!skipAnswersCheck &&
                    (!question.question.answers || question.question.answers.length === 0)) {
                    return questions;
                }
                const triggers = question.triggers ? [...question.triggers] : [];
                triggers.push({
                    answerValueIds: [],
                    toQuestionId: undefined,
                    action: WorkflowTriggerAction.GO_TO,
                    triggerPhase: "POST",
                    triggerOrder: triggers.length,
                    _clientId: generateTriggerClientId(),
                    compoundCondition: {
                        operator: "AND",
                        conditions: [{ questionId: question.question.id, answerValueIds: [] }],
                    },
                });
                const updated = [...questions];
                updated[index] = { ...question, triggers };
                return updated;
            };

            return {
                ...prev,
                systemQuestions: updateQuestions(prev.systemQuestions),
                workflowQuestions: updateQuestions(prev.workflowQuestions),
            };
        });
    };

    const handleAddShowWhen = (clientId: string) => {
        setFormState((prev) => {
            const updateQuestions = (questions: WorkflowQuestionWithClientId[]) => {
                const index = questions.findIndex((q) => q.clientId === clientId);
                if (index === -1) return questions;
                const question = questions[index];
                const triggers = question.triggers ? [...question.triggers] : [];
                triggers.push({
                    answerValueIds: [],
                    action: WorkflowTriggerAction.SHOW_QUESTION,
                    triggerPhase: "PRE",
                    triggerOrder: triggers.length,
                    _clientId: generateTriggerClientId(),
                    compoundCondition: {
                        operator: "AND",
                        conditions: [{ questionId: question.question.id, answerValueIds: [] }],
                    },
                });
                const updated = [...questions];
                updated[index] = { ...question, triggers };
                return updated;
            };

            return {
                ...prev,
                systemQuestions: updateQuestions(prev.systemQuestions),
                workflowQuestions: updateQuestions(prev.workflowQuestions),
            };
        });
    };

    const handleRemoveTrigger = (clientId: string, triggerIndex: number) => {
        setFormState((prev) => {
            const updateQuestions = (questions: WorkflowQuestionWithClientId[]) => {
                const index = questions.findIndex((q) => q.clientId === clientId);
                if (index === -1) return questions;
                const question = questions[index];
                const triggers = question.triggers
                    ? question.triggers
                        .filter((_, idx) => idx !== triggerIndex)
                        .map((t, idx) => ({ ...t, triggerOrder: idx }))
                    : [];
                const updated = [...questions];
                updated[index] = { ...question, triggers };
                return updated;
            };
            
            return {
                ...prev,
                systemQuestions: updateQuestions(prev.systemQuestions),
                workflowQuestions: updateQuestions(prev.workflowQuestions),
            };
        });
    };

    const handleTriggerAnswersChange = (clientId: string, triggerIndex: number, values: AnswerValue[]) => {
        setFormState((prev) => {
            const updateQuestions = (questions: WorkflowQuestionWithClientId[]) => {
                const index = questions.findIndex((q) => q.clientId === clientId);
                if (index === -1) return questions;
                const question = questions[index];
                const triggers = question.triggers ? [...question.triggers] : [];
                triggers[triggerIndex] = {
                    ...triggers[triggerIndex],
                    answerValueIds: values.map((value) => value.id as number),
                };
                const updated = [...questions];
                updated[index] = { ...question, triggers };
                return updated;
            };
            
            return {
                ...prev,
                systemQuestions: updateQuestions(prev.systemQuestions),
                workflowQuestions: updateQuestions(prev.workflowQuestions),
            };
        });
    };

    const handleTriggerTargetChange = (clientId: string, triggerIndex: number, targetId: number | undefined) => {
        setFormState((prev) => {
            const updateQuestions = (questions: WorkflowQuestionWithClientId[]) => {
                const index = questions.findIndex((q) => q.clientId === clientId);
                if (index === -1) return questions;
                const question = questions[index];
                const triggers = question.triggers ? [...question.triggers] : [];
                const existingTrigger = triggers[triggerIndex];
                const previousTargetId = existingTrigger?.toQuestionId;
                triggers[triggerIndex] = {
                    ...existingTrigger,
                    toQuestionId: targetId,
                    // Wipe stale answer ids when the target changes (or is
                    // cleared) — they belong to the previous target's answer
                    // set and would otherwise be saved as a mismatched payload.
                    toAnswerValueIds:
                        previousTargetId !== targetId ? [] : existingTrigger?.toAnswerValueIds,
                };
                const updated = [...questions];
                updated[index] = { ...question, triggers };
                return updated;
            };
            
            return {
                ...prev,
                systemQuestions: updateQuestions(prev.systemQuestions),
                workflowQuestions: updateQuestions(prev.workflowQuestions),
            };
        });
    };

    const handleTriggerToAnswersChange = (clientId: string, triggerIndex: number, answerIds: number[]) => {
        setFormState((prev) => {
            const updateQuestions = (questions: WorkflowQuestionWithClientId[]) => {
                const index = questions.findIndex((q) => q.clientId === clientId);
                if (index === -1) return questions;
                const question = questions[index];
                const triggers = question.triggers ? [...question.triggers] : [];
                triggers[triggerIndex] = {
                    ...triggers[triggerIndex],
                    toAnswerValueIds: answerIds,
                };
                const updated = [...questions];
                updated[index] = { ...question, triggers };
                return updated;
            };
            
            return {
                ...prev,
                systemQuestions: updateQuestions(prev.systemQuestions),
                workflowQuestions: updateQuestions(prev.workflowQuestions),
            };
        });
    };

    const handleConditionClauseChange = (
        clientId: string,
        triggerIndex: number,
        clauseIndex: number,
        field: keyof ConditionClause,
        value: number | number[],
    ) => {
        setFormState((prev) => {
            const updateQuestions = (questions: WorkflowQuestionWithClientId[]) => {
                const index = questions.findIndex((q) => q.clientId === clientId);
                if (index === -1) return questions;
                const question = questions[index];
                const triggers = question.triggers ? [...question.triggers] : [];
                const trigger = triggers[triggerIndex];
                if (!trigger?.compoundCondition) {
                    // Legacy trigger: update answerValueIds directly
                    if (field === "answerValueIds" && Array.isArray(value)) {
                        triggers[triggerIndex] = {
                            ...trigger,
                            answerValueIds: value,
                        };
                    }
                    const updated = [...questions];
                    updated[index] = { ...question, triggers };
                    return updated;
                }
                const conditions = [...trigger.compoundCondition.conditions];
                if (field === "questionId") {
                    conditions[clauseIndex] = { questionId: value as number, answerValueIds: [] };
                } else {
                    conditions[clauseIndex] = {
                        ...conditions[clauseIndex],
                        answerValueIds: value as number[],
                    };
                }
                triggers[triggerIndex] = {
                    ...trigger,
                    compoundCondition: { ...trigger.compoundCondition, conditions },
                };
                const updated = [...questions];
                updated[index] = { ...question, triggers };
                return updated;
            };
            return {
                ...prev,
                systemQuestions: updateQuestions(prev.systemQuestions),
                workflowQuestions: updateQuestions(prev.workflowQuestions),
            };
        });
    };

    const handleAddConditionClause = (clientId: string, triggerIndex: number) => {
        setFormState((prev) => {
            const updateQuestions = (questions: WorkflowQuestionWithClientId[]) => {
                const index = questions.findIndex((q) => q.clientId === clientId);
                if (index === -1) return questions;
                const question = questions[index];
                const triggers = question.triggers ? [...question.triggers] : [];
                const trigger = triggers[triggerIndex];
                if (!trigger?.compoundCondition) return questions;
                const conditions = [
                    ...trigger.compoundCondition.conditions,
                    { questionId: question.question.id, answerValueIds: [] } as ConditionClause,
                ];
                triggers[triggerIndex] = {
                    ...trigger,
                    compoundCondition: { ...trigger.compoundCondition, conditions },
                };
                const updated = [...questions];
                updated[index] = { ...question, triggers };
                return updated;
            };
            return {
                ...prev,
                systemQuestions: updateQuestions(prev.systemQuestions),
                workflowQuestions: updateQuestions(prev.workflowQuestions),
            };
        });
    };

    const handleRemoveConditionClause = (clientId: string, triggerIndex: number, clauseIndex: number) => {
        setFormState((prev) => {
            const updateQuestions = (questions: WorkflowQuestionWithClientId[]) => {
                const index = questions.findIndex((q) => q.clientId === clientId);
                if (index === -1) return questions;
                const question = questions[index];
                const triggers = question.triggers ? [...question.triggers] : [];
                const trigger = triggers[triggerIndex];
                if (!trigger?.compoundCondition) return questions;
                const conditions = trigger.compoundCondition.conditions.filter((_, i) => i !== clauseIndex);
                triggers[triggerIndex] = {
                    ...trigger,
                    compoundCondition: { ...trigger.compoundCondition, conditions },
                };
                const updated = [...questions];
                updated[index] = { ...question, triggers };
                return updated;
            };
            return {
                ...prev,
                systemQuestions: updateQuestions(prev.systemQuestions),
                workflowQuestions: updateQuestions(prev.workflowQuestions),
            };
        });
    };

    const handleOperatorChange = (clientId: string, triggerIndex: number, operator: CompoundCondition["operator"]) => {
        setFormState((prev) => {
            const updateQuestions = (questions: WorkflowQuestionWithClientId[]) => {
                const index = questions.findIndex((q) => q.clientId === clientId);
                if (index === -1) return questions;
                const question = questions[index];
                const triggers = question.triggers ? [...question.triggers] : [];
                const trigger = triggers[triggerIndex];
                if (!trigger?.compoundCondition) return questions;
                triggers[triggerIndex] = {
                    ...trigger,
                    compoundCondition: { ...trigger.compoundCondition, operator },
                };
                const updated = [...questions];
                updated[index] = { ...question, triggers };
                return updated;
            };
            return {
                ...prev,
                systemQuestions: updateQuestions(prev.systemQuestions),
                workflowQuestions: updateQuestions(prev.workflowQuestions),
            };
        });
    };

    const handleExpressionChange = (clientId: string, expression: string) => {
        setFormState((prev) => {
            const updateQuestions = (questions: WorkflowQuestionWithClientId[]) => {
                const index = questions.findIndex((q) => q.clientId === clientId);
                if (index === -1) return questions;
                const question = questions[index];
                const updated = [...questions];
                updated[index] = {
                    ...question,
                    question: {
                        ...question.question,
                        expression,
                    },
                };
                return updated;
            };
            return {
                ...prev,
                systemQuestions: updateQuestions(prev.systemQuestions),
                workflowQuestions: updateQuestions(prev.workflowQuestions),
            };
        });
    };

    const handleSpanScopeChange = (
        clientId: string,
        spanScope: SpanWorkflowQuestionScope,
        spanScopeQuestionId?: number | null,
    ) => {
        setFormState((prev) => {
            const updateQuestions = (questions: WorkflowQuestionWithClientId[]) => questions.map((question) => {
                if (question.clientId !== clientId) {
                    return question;
                }
                return {
                    ...question,
                    spanScope,
                    spanScopeQuestionId: spanScope === "SELECTED_BY_SPANS_QUESTION"
                        ? spanScopeQuestionId ?? null
                        : null,
                };
            });
            return {
                ...prev,
                systemQuestions: updateQuestions(prev.systemQuestions),
                workflowQuestions: updateQuestions(prev.workflowQuestions),
            };
        });
    };

    const handleTriggerActionChange = (
        clientId: string,
        triggerIndex: number,
        action: WorkflowTriggerAction
    ) => {
        setFormState((prev) => {
            // The Target Question picker scopes options by the host question's
            // section: system-question hosts can target system + workflow
            // questions, workflow-question hosts can only target other workflow
            // questions, and no host can target itself. Mirror that scope here
            // when validating a surviving toQuestionId so an action change
            // doesn't preserve a target the picker can no longer display.
            const isSystemHost = prev.systemQuestions.some((q) => q.clientId === clientId);
            const hostQuestionIds = getFormQuestionIds([...prev.systemQuestions, ...prev.workflowQuestions]);
            const targetPoolForHost = (
                hostQuestionId: number | undefined,
                nextAction: WorkflowTriggerAction,
            ): Set<number> => {
                const crossWorkflowCandidates =
                    supportsCrossWorkflowTargets(nextAction) ? crossWorkflowQuestions : [];
                const candidates = isSystemHost
                    ? [...prev.systemQuestions, ...prev.workflowQuestions, ...crossWorkflowCandidates]
                    : [...prev.workflowQuestions, ...crossWorkflowCandidates];
                return new Set<number>(
                    candidates
                        .map((q) => q.question?.id)
                        .filter((id): id is number => id != null && id !== hostQuestionId),
                );
            };
            const updateQuestions = (questions: WorkflowQuestionWithClientId[]) => {
                const index = questions.findIndex((q) => q.clientId === clientId);
                if (index === -1) return questions;
                const question = questions[index];
                const triggers = question.triggers ? [...question.triggers] : [];
                const currentTrigger = triggers[triggerIndex] || { answerValueIds: [] };
                const needsTarget = action === "GO_TO" || action === "SET_ANSWERS" || action === "AUTOFILL";
                const needsToAnswers = action === "SET_ANSWERS" || action === "AUTOFILL";

                // Compute the resolved target id. STOP and !needsTarget always
                // wipe; for needsTarget actions, drop a stale toQuestionId that
                // is not in the new action's option set so it doesn't survive
                // hidden in state and get persisted on Save.
                const allowedTargets = targetPoolForHost(question.question?.id, action);
                let resolvedTargetId: number | undefined;
                if (action === "STOP" || !needsTarget) {
                    resolvedTargetId = undefined;
                } else if (
                    currentTrigger.toQuestionId != null &&
                    allowedTargets.has(currentTrigger.toQuestionId)
                ) {
                    resolvedTargetId = currentTrigger.toQuestionId;
                } else {
                    resolvedTargetId = undefined;
                }

                // Compute the resolved toAnswerValueIds. AUTOFILL stores a
                // single answer (its UI renders only `toAnswerValueIds?.[0]`),
                // so a transition from SET_ANSWERS with multiple answers must
                // not leave the extras as hidden state that survives on Save.
                let resolvedToAnswers: number[] | undefined;
                if (!needsToAnswers) {
                    resolvedToAnswers = undefined;
                } else if (resolvedTargetId !== currentTrigger.toQuestionId) {
                    // Target was dropped or changed; the answer ids referenced
                    // the previous target's answer set.
                    resolvedToAnswers = [];
                } else if (action === "AUTOFILL") {
                    // Truncate to the single-answer shape AUTOFILL renders.
                    const existing = currentTrigger.toAnswerValueIds ?? [];
                    resolvedToAnswers = existing.length > 0 ? [existing[0]] : [];
                } else {
                    resolvedToAnswers = currentTrigger.toAnswerValueIds ?? [];
                }

                const nextTrigger = {
                    ...currentTrigger,
                    answerValueIds: currentTrigger.answerValueIds || [],
                    action,
                    toQuestionId: resolvedTargetId,
                    toAnswerValueIds: resolvedToAnswers,
                };
                triggers[triggerIndex] = sanitizeTriggerSourceScope(
                    nextTrigger,
                    question.question.id,
                    hostQuestionIds,
                    getHostAnswerValueIds(question),
                    supportsCrossWorkflowSources(action, "POST", prev.codeType),
                );
                const updated = [...questions];
                updated[index] = { ...question, triggers };
                return updated;
            };
            
            return {
                ...prev,
                systemQuestions: updateQuestions(prev.systemQuestions),
                workflowQuestions: updateQuestions(prev.workflowQuestions),
            };
        });
    };

    const handleEditWorkflow = async (workflow: CodeWorkflow) => {
        if (!workflow.id) {
            return;
        }
        setDetailLoading(true);
        setFormError(null);
        setDetailLoadFailed(false);
        // Switch to the detail view first with the list-level workflow data so
        // the panel renders the editor shell — and any error alert raised below
        // — even when the detail fetch fails. Without this the user clicks the
        // edit pencil, the GET /code-workflows/{id} returns non-2xx, and the
        // panel stays parked on the list with no feedback.
        const codeType = (workflow.codeType as CodeWorkflowType) ?? "PROCEDURE";
        const initialQuestions = withClientIds(workflow.questions ?? []);
        setSelectedWorkflow(workflow);
        setFormState({
            name: workflow.name ?? "",
            description: workflow.description ?? "",
            codeType,
            active: workflow.active ?? true,
            systemQuestions: initialQuestions.filter(q => q.question.questionType === QuestionType.SYSTEM),
            workflowQuestions: initialQuestions.filter(q => q.question.questionType !== QuestionType.SYSTEM),
        });
        setViewMode("detail");
        try {
            const detail = await fetchCodeWorkflow(workflow.id);
            setDetailLoadFailed(false);
            setSelectedWorkflow(detail);
            const detailCodeType = (detail.codeType as CodeWorkflowType) ?? "PROCEDURE";
            const allQuestions = withClientIds(detail.questions);
            setFormState({
                name: detail.name ?? "",
                description: detail.description ?? "",
                codeType: detailCodeType,
                active: detail.active ?? true,
                systemQuestions: allQuestions.filter(q => q.question.questionType === QuestionType.SYSTEM),
                workflowQuestions: allQuestions.filter(q => q.question.questionType !== QuestionType.SYSTEM),
            });

            // If the workflow references questions not in the active list, fetch and add them
            const workflowQuestionIds = (detail.questions ?? [])
                .map((wq) => wq.question?.id)
                .filter((id): id is number => id != null);
            const missingQuestionIds = workflowQuestionIds.filter(
                (id) => !questions.some((q) => q.id === id)
            );
            if (missingQuestionIds.length > 0) {
                const inactiveQuestions = await Promise.all(
                    missingQuestionIds.map((id) =>
                        fetchQuestion(id).catch((err) => {
                            console.warn(`Could not fetch inactive question ${id}`, err);
                            return null;
                        })
                    )
                );
                const validQuestions = inactiveQuestions.filter(
                    (q): q is Question => q !== null
                );
                if (validQuestions.length > 0) {
                    setQuestions((prev) => [...prev, ...validQuestions]);
                }
            }
        } catch (err) {
            console.error("Failed to load workflow detail", err);
            setFormError("Unable to load workflow");
            setDetailLoadFailed(true);
        } finally {
            setDetailLoading(false);
        }
    };

    const handleSaveWorkflow = async () => {
        if (!formState.name.trim()) {
            setFormError("Name is required");
            return;
        }

        const allQuestions = [
            ...formState.systemQuestions,
            ...formState.workflowQuestions,
        ].map((q, idx) => ({
            ...q,
            sequenceOrder: idx + 1,
        }));

        // [CDMX-14572] Cross-workflow SOURCE picker for SPAN-host workflows allows
        // trigger conditions to reference questions on sibling code/case workflows.
        // Pass only ids that are actually selected on phase/action-supported
        // triggers so stale POST trigger state cannot keep cross-workflow sources.
        const extraValidQuestionIds = new Set<number>([
            ...getSelectedCrossWorkflowSourceQuestionIds(allQuestions, formState.codeType),
        ]);
        const validationError = validateWorkflowBeforeSave(allQuestions, extraValidQuestionIds);
        if (validationError) {
            setFormError(validationError);
            return;
        }
        if (formState.codeType === "SPAN") {
            const missingSpanScopeSource = allQuestions.find(
                (q) => q.spanScope === "SELECTED_BY_SPANS_QUESTION" && q.spanScopeQuestionId == null,
            );
            if (missingSpanScopeSource) {
                setFormError(`Question "${missingSpanScopeSource.question.name}" must choose a source SPANS question`);
                return;
            }
        }

        setSaving(true);
        setFormError(null);
        try {
            const questionsPayload = sanitizeWorkflowQuestionsForSave(
                allQuestions.map(({ clientId, ...question }) => ({
                    ...question,
                }))
            );
            const payload = {
                name: formState.name.trim(),
                codeType: formState.codeType,
                description: formState.description?.trim() || undefined,
                active: formState.active,
                questions: questionsPayload,                
            };
            if (selectedWorkflow?.id) {
                await updateCodeWorkflow(selectedWorkflow.id, payload);
            } else {
                await createCodeWorkflow(payload);
            }
            await loadWorkflows();
            handleBackToList();
        } catch (err) {
            console.error("Failed to save workflow", err);
            const errorMessage = err instanceof Error && err.message 
                ? `Unable to save workflow: ${err.message}`
                : "Unable to save workflow";
            setFormError(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteWorkflow = async (workflow: CodeWorkflow) => {
        if (!workflow.id) {
            return;
        }
        const confirmed = window.confirm(`Delete code workflow "${workflow.name}"?`);
        if (!confirmed) {
            return;
        }
        try {
            await deleteCodeWorkflow(workflow.id);
            await loadWorkflows();
        } catch (err) {
            console.error("Failed to delete workflow", err);
            setError(err instanceof Error ? err.message : "Unable to delete workflow");
        }
    };

    const handleToggleActive = async (workflow: CodeWorkflow, active: boolean) => {
        if (!workflow.id) {
            return;
        }
        try {
            await updateCodeWorkflow(workflow.id, {
                name: workflow.name,
                codeType: workflow.codeType as CodeWorkflowType,
                description: workflow.description,
                active,
            });
            await loadWorkflows();
        } catch (err) {
            console.error("Failed to toggle workflow active status", err);
            setError(err instanceof Error ? err.message : "Unable to update workflow");
        }
    };

    const renderActions = (workflow: CodeWorkflow) => (
        <>
            <Tooltip title="Edit workflow">
                <IconButton
                    onClick={(event) => {
                        event.stopPropagation();
                        handleEditWorkflow(workflow);
                    }}
                    sx={{ color: "#7498A6" }}
                >
                    <EditOutlined fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Delete workflow">
                <IconButton
                    onClick={(event) => {
                        event.stopPropagation();
                        handleDeleteWorkflow(workflow);
                    }}
                    sx={{ color: "#7498A6" }}
                >
                    <DeleteOutline fontSize="small" />
                </IconButton>
            </Tooltip>
        </>
    );

    const renderList = () => (
        <RubricManagementList<CodeWorkflow>
            items={workflows}
            totalCount={totalCount}
            loading={loading}
            error={error}
            listState={listState}
            columns={workflowColumns}
            getItemKey={(item) => item.id ?? item.name}
            onItemClick={handleEditWorkflow}
            onAddClick={handleCreateNew}
            searchPlaceholder="Search workflows by name or description..."
            addButtonLabel="Add Workflow"
            itemLabel="workflows"
            emptyMessage="No workflows found."
            renderActions={renderActions}
            statusFilter={statusFilter}
            onStatusFilterChange={(value: StatusFilter) => { setStatusFilter(value); listState.setPage(0); }}
            onToggleActive={handleToggleActive}
            isItemActive={(item) => Boolean(item.active)}
        />
    );

    const toggleQuestionSection = (questionKey: string) => {
        setCollapsedSections((prev) => ({ ...prev, [questionKey]: !prev[questionKey] }));
    };

    const renderDetail = () => (
        <Grid container gap={2}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} width="100%" alignItems="center">
                <Button variant={"outlined"} startIcon={<ArrowBack />} onClick={handleBackToList}>
                    Back to Workflows
                </Button>
                <Box flexGrow={1} />
                <Stack direction="row" spacing={1}>
                    <Button variant={'outlined'} onClick={handleBackToList}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveWorkflow}
                        disabled={
                            saving ||
                            detailLoading ||
                            crossWorkflowQuestionsLoading ||
                            (selectedWorkflow?.id != null && detailLoadFailed)
                        }
                    >
                        {saving ? "Saving..." : "Save Workflow"}
                    </Button>
                </Stack>
            </Stack>

            {formError ? <Alert severity="error">{formError}</Alert> : null}

            {detailLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Stack direction="column" spacing={2} width="100%">
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                label="Name"
                                required
                                value={formState.name}
                                fullWidth
                                onChange={(event) => setFormState((prev) => ({ ...prev, name: event.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Description"
                                multiline
                                minRows={2}
                                value={formState.description}
                                fullWidth
                                onChange={(event) => setFormState((prev) => ({ ...prev, description: event.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel id="code-type-label">Code Type</InputLabel>
                                <Select
                                    labelId="code-type-label"
                                    label="Code Type"
                                    value={formState.codeType}
                                    onChange={(event) => {
                                        const newCodeType = event.target.value as CodeWorkflowType;
                                        setFormState((prev) => {
                                            const hostQuestionIds = getFormQuestionIds([
                                                ...prev.systemQuestions,
                                                ...prev.workflowQuestions,
                                            ]);
                                            const normalizeForCodeType = (question: WorkflowQuestionWithClientId): WorkflowQuestionWithClientId => {
                                                const sourceScopedQuestion = sanitizeWorkflowQuestionSourceScope(
                                                    question,
                                                    hostQuestionIds,
                                                    newCodeType,
                                                );
                                                if (newCodeType === "SPAN") {
                                                    return sourceScopedQuestion;
                                                }

                                                return {
                                                    ...removePreShowQuestionTriggers(sourceScopedQuestion),
                                                    spanScope: "ALL_SPANS",
                                                    spanScopeQuestionId: null,
                                                };
                                            };
                                            return {
                                                ...prev,
                                                codeType: newCodeType,
                                                systemQuestions: prev.systemQuestions.map((question) =>
                                                    normalizeForCodeType(question)
                                                ),
                                                workflowQuestions: prev.workflowQuestions.map((question) =>
                                                    normalizeForCodeType(question)
                                                ),
                                            };
                                        });
                                    }}
                                >
                                    {codeTypeOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Divider />
                    <Grid container spacing={2} width="100%">
                        <Stack direction="row" spacing={2} alignItems="center" width="100%" justifyContent="space-between">
                            <Typography variant="subtitle1" sx={{ mb: 2 }}>System Questions</Typography>
                            <IconButton
                                onClick={() => setSystemQuestionsSectionCollapsed(!systemQuestionsSectionCollapsed)}
                                size="small"
                            >
                                {systemQuestionsSectionCollapsed ? <ExpandMore /> : <ExpandLess />}
                            </IconButton>
                        </Stack>
                        
                        {!systemQuestionsSectionCollapsed && (
                            <QuestionsSection
                                questions={formState.systemQuestions}
                                validTargetQuestions={allFormQuestions}
                                crossWorkflowTargetQuestions={crossWorkflowQuestions}
                                hostCodeType={formState.codeType}
                                crossWorkflowSourceQuestions={crossWorkflowSourceQuestions}
                                allQuestions={allFormQuestions}
                                globalQuestionPool={questions}
                                onDragEnd={handleSystemQuestionDragEnd}
                                collapsedSections={collapsedSections}
                                onToggleSection={toggleQuestionSection}
                                onRemoveQuestion={handleRemoveQuestion}
                                onAddTrigger={handleAddTrigger}
                                onAddShowWhen={handleAddShowWhen}
                                onRemoveTrigger={handleRemoveTrigger}
                                onTriggerAnswersChange={handleTriggerAnswersChange}
                                onTriggerTargetChange={handleTriggerTargetChange}
                                onTriggerActionChange={handleTriggerActionChange}
                                onTriggerToAnswersChange={handleTriggerToAnswersChange}
                                onConditionClauseChange={handleConditionClauseChange}
                                onAddConditionClause={handleAddConditionClause}
                                onRemoveConditionClause={handleRemoveConditionClause}
                                onOperatorChange={handleOperatorChange}
                                onAddQuestion={handleAddQuestion}
                                showAddQuestion={true}
                                questionsAvailableToAdd={availableSystemQuestions}
                                droppableId="system-questions"
                                onTriggerDragEnd={handleTriggerDragEnd}
                                onExpressionChange={handleExpressionChange}
                                onSpanScopeChange={handleSpanScopeChange}
                            />
                        )}
                    </Grid>                   

                    <Divider />

                     <Grid container spacing={2} width="100%">
                        <Stack direction="row" spacing={2} alignItems="center" width="100%" justifyContent="space-between">
                            <Typography variant="subtitle1" sx={{ mb: 2 }}>Workflow Questions</Typography>
                            <IconButton
                                onClick={() => setQuestionsSectionCollapsed(!questionsSectionCollapsed)}
                                size="small"
                            >
                                {questionsSectionCollapsed ? <ExpandMore /> : <ExpandLess />}
                            </IconButton>
                        </Stack>
                        
                        {!questionsSectionCollapsed && (
                            <QuestionsSection
                                questions={formState.workflowQuestions}
                                validTargetQuestions={formState.workflowQuestions}
                                crossWorkflowTargetQuestions={crossWorkflowQuestions}
                                hostCodeType={formState.codeType}
                                crossWorkflowSourceQuestions={crossWorkflowSourceQuestions}
                                allQuestions={allFormQuestions}
                                globalQuestionPool={questions}
                                onDragEnd={handleWorkflowQuestionDragEnd}
                                collapsedSections={collapsedSections}
                                onToggleSection={toggleQuestionSection}
                                onRemoveQuestion={handleRemoveQuestion}
                                onAddTrigger={handleAddTrigger}
                                onAddShowWhen={handleAddShowWhen}
                                onRemoveTrigger={handleRemoveTrigger}
                                onTriggerAnswersChange={handleTriggerAnswersChange}
                                onTriggerTargetChange={handleTriggerTargetChange}
                                onTriggerActionChange={handleTriggerActionChange}
                                onTriggerToAnswersChange={handleTriggerToAnswersChange}
                                onConditionClauseChange={handleConditionClauseChange}
                                onAddConditionClause={handleAddConditionClause}
                                onRemoveConditionClause={handleRemoveConditionClause}
                                onOperatorChange={handleOperatorChange}
                                questionsAvailableToAdd={availableWorkflowQuestions}
                                onAddQuestion={handleAddQuestion}
                                droppableId="workflow-questions"
                                onTriggerDragEnd={handleTriggerDragEnd}
                                onExpressionChange={handleExpressionChange}
                                onSpanScopeChange={handleSpanScopeChange}
                            />
                        )}
                    </Grid>

                </Stack>
            )}
        </Grid>
    );

    return <Box>{viewMode === "list" ? renderList() : renderDetail()}</Box>;
};

export default CodeWorkflowsPanel;
